/* Created by Interactive 63 - https://interactive63.com */
/**
 * Autosave: writes the open project to IndexedDB shortly after each change, and straight away
 * when the page is hidden. Failures never interrupt editing: they are reported once through the
 * warning callback and the status shows "Not saved".
 */
import type { Project } from '../../core/model/types';
import type { Store } from '../store/store';
import type { ProjectStorage } from './project-storage';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'unsaved';

export interface AutosaverOptions {
  debounceMs?: number;
  thumbnail: (project: Project) => string | null;
  onSaved?: (projectId: string) => void;
  onWarning?: (message: string) => void;
}

export const AUTOSAVE_UNAVAILABLE =
  "Autosave isn't available in this browser window. Use Save project to keep a copy.";

export class Autosaver {
  private dirty = false;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private clean: Project | undefined;
  private warned = false;
  private persistRequested = false;
  private status: SaveStatus = 'idle';
  private readonly listeners: ((status: SaveStatus) => void)[] = [];
  private inFlight: Promise<void> = Promise.resolve();

  /** Creates an autosaver for the store's project. Call `start` to begin watching. */
  constructor(
    private readonly store: Store,
    private readonly storage: ProjectStorage,
    private readonly options: AutosaverOptions,
  ) {}

  /** Begins watching the store and the page lifecycle. Returns a function that stops it. */
  start(): () => void {
    const unsubscribe = this.store.select(
      (s) => s.project,
      (project) => {
        if (project === this.clean || !this.hasContent(project)) return;
        this.dirty = true;
        this.setStatus('saving');
        this.schedule();
      },
    );
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') void this.flush();
    };
    const onPageHide = (): void => {
      void this.flush();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      clearTimeout(this.timer);
    };
  }

  /** Registers a status listener and reports the current status straight away. */
  onStatus(listener: (status: SaveStatus) => void): void {
    this.listeners.push(listener);
    listener(this.status);
  }

  /** Treats the store's current project as already saved, for projects just loaded from storage. */
  markClean(): void {
    clearTimeout(this.timer);
    this.dirty = false;
    this.clean = this.store.getState().project;
    this.setStatus(this.hasContent(this.clean) ? 'saved' : 'idle');
  }

  /** Writes any pending change now. */
  async flush(): Promise<void> {
    clearTimeout(this.timer);
    if (!this.dirty) {
      await this.inFlight;
      return;
    }
    await this.write();
  }

  /** Writes the current project now whether or not it has changed. */
  async saveNow(): Promise<void> {
    this.dirty = true;
    await this.flush();
  }

  /** Whether a project has anything worth saving. */
  private hasContent(project: Project): boolean {
    return Object.keys(project.elements).length > 0;
  }

  /** Restarts the debounce timer. */
  private schedule(): void {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      void this.flush();
    }, this.options.debounceMs ?? 1000);
  }

  /** Saves the current project, then records it as the project to reopen. */
  private write(): Promise<void> {
    const project = this.store.getState().project;
    this.dirty = false;
    this.setStatus('saving');
    this.inFlight = this.inFlight.then(async () => {
      try {
        await this.storage.save(project, this.options.thumbnail(project));
        await this.storage.setLastOpenId(project.id);
        this.options.onSaved?.(project.id);
        this.setStatus(this.dirty ? 'saving' : 'saved');
        this.requestPersistence();
      } catch (error) {
        console.warn('Autosave failed', error);
        this.dirty = true;
        this.setStatus('unsaved');
        if (!this.warned) {
          this.warned = true;
          this.options.onWarning?.(AUTOSAVE_UNAVAILABLE);
        }
      }
    });
    return this.inFlight;
  }

  /** Asks the browser once to keep this origin's storage; the answer is not needed. */
  private requestPersistence(): void {
    if (this.persistRequested) return;
    this.persistRequested = true;
    try {
      void navigator.storage.persist().catch(() => undefined);
    } catch {
      // Not supported in this browser.
    }
  }

  /** Updates the status and informs listeners when it changes. */
  private setStatus(status: SaveStatus): void {
    if (status === this.status) return;
    this.status = status;
    for (const listener of this.listeners) listener(status);
  }
}
